// src/components/leaves/UrgentLeaveRequests.jsx
import React, { useState, useEffect } from 'react';
import { Eye } from 'lucide-react';
import { formatDate, calculateBusinessDays } from '../leaves/helpers'; // adjust path
import { getPendingRequestsStartingTomorrow, getPendingRequestsStartingNextWeek, approveLeave, rejectLeave } from '../../services/leavesAPIs';
import { Alert } from '@mui/material';

const UrgentLeaveRequests = ({
  setSelectedLeave,
  fetchData,
  IsEmployee
}) => {
  const [tomorrowLeaves, setTomorrowLeaves] = useState([]);
  const [nextWeekLeaves, setNextWeekLeaves] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(true);

  const leaveTypeLabels = {
    SICK_LEAVE: 'Sick Leave',
    ANNUAL_LEAVE: 'Annual Leave',
    UNPAID_LEAVE: 'Unpaid Leave',
    MATERNITY_LEAVE: 'Maternity Leave',
  };

  const getApiErrorMessage = (error, fallbackMessage) => {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string' && responseData.trim()) {
      return responseData;
    }
    if (responseData?.message) {
      return responseData.message;
    }
    if (responseData?.error) {
      return responseData.error;
    }
    return error?.message || fallbackMessage;
  };

  // Handle approval
  const handleApprove = async (leaveId) => {
    try {
      await approveLeave(leaveId);
      setSuccess('Leave approved successfully.');
      fetchData(); // refresh main list and urgent list
      fetchUrgent();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to approve leave.');
      setError(msg.includes('contract') ? 'Cannot approve: Employee has no contract.' : msg);
      setTimeout(() => setError(''), 4000);
    }
  };

  // Handle rejection (simple — no dialog for urgency)
  const handleReject = async (leaveId) => {
    const reason = prompt('Please provide a short rejection reason:');
    if (!reason?.trim()) return;

    try {
      await rejectLeave(leaveId, { reason: reason.trim() });
      setSuccess('Leave rejected successfully.');
      fetchData();
      fetchUrgent();
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to reject leave.');
      setError(msg.includes('contract') ? 'Cannot reject: Employee has no contract.' : msg);
      setTimeout(() => setError(''), 4000);
    }
  };

  const fetchUrgent = async () => {
    setLoading(true);
    setError('');
    try {
      const tomorrow = await getPendingRequestsStartingTomorrow();
      const nextWeek = await getPendingRequestsStartingNextWeek();
      setTomorrowLeaves(tomorrow);
      setNextWeekLeaves(nextWeek);
    } catch (err) {
      console.error('Failed to load urgent leaves:', err);
      setError('Failed to load urgent leave requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (IsEmployee) return;
    fetchUrgent();
  }, [IsEmployee]);

  if (IsEmployee || (tomorrowLeaves.length === 0 && nextWeekLeaves.length === 0)) {
    return null;
  }

  const renderTable = (leaves, urgencyType) => {
    if (leaves.length === 0) return null;

    const isTomorrow = urgencyType === 'tomorrow';
    const title = isTomorrow ? 'Starting Tomorrow' : 'Starting Next Week';
    const badgeColor = isTomorrow ? '#c92a2a' : '#e67700';

    return (
      <div className="Urgent-section" style={{ marginBottom: '32px' }}>
        <div className="Urgent-section-header" style={{ marginBottom: '16px' }}>
          <h4 className="Urgent-section-title">
            <span className="Urgent-badge" style={{ backgroundColor: badgeColor }}>
              {title}
            </span>
          </h4>
        </div>
        <div className="Urgent-table-wrapper">
          <table className="Urgent-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Office</th>
                <th>Leave Type</th>
                <th>Start Date</th>
                <th>End Date</th>
                <th>Duration</th>
                <th>Description</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map(leave => (
                <tr key={leave.requestId} className="Urgent-table-row">
                  <td>{leave.employeeFirstName || 'Unknown'} {leave.employeeLastName || 'Unknown'}</td>
                  <td>{leave.departmentName || 'N/A'}</td>
                  <td>{leave.officeName || 'N/A'}</td>
                  <td>
                    <span className="Urgent-leave-type">
                      {leaveTypeLabels[leave.type] || leave.type}
                    </span>
                  </td>
                  <td>{formatDate(leave.startDate)}</td>
                  <td>to {formatDate(leave.endDate)}</td>
                  <td>
                     {leave.days} {leave.days === 1 ? 'day' : 'days'}
                  </td>
                  <td style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {leave.justification || '—'}
                  </td>
                  <td>
                    <div className="Urgent-actions">
                      <button
                        onClick={() => setSelectedLeave(leave.requestId)}
                        className="Urgent-btn-view"
                        title="View Details"
                      >
                        <Eye size={18} />
                      </button>

                      {/* Approve / Reject — only if pending */}
                      {leave.status === 'pending' && !IsEmployee && (
                        <div className="Urgent-action-buttons">
                          <button
                            onClick={() => handleApprove(leave.requestId)}
                            className="Urgent-btn-approve shake"
                            title="Approve"
                          >
                            ✓
                          </button>
                          <button
                            onClick={() => handleReject(leave.requestId)}
                            className="Urgent-btn-reject shake"
                            title="Reject"
                          >
                            ✗
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="Urgent-requests-container">
      <div className="Urgent-main-header">
        <h3 className="Urgent-main-title">❗ Urgent Leave Requests</h3>
        {error && (
          <Alert severity="error" sx={{ mb: 2, maxWidth: 800 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2, maxWidth: 800 }}>
            {success}
          </Alert>
        )}
      </div>

      {renderTable(tomorrowLeaves, 'tomorrow')}
      {renderTable(nextWeekLeaves, 'next-week')}

      <style jsx>{`
        .Urgent-requests-container {
          background: var(--card-bg, white);
          border-radius: 16px;
          padding: 24px;
          margin: 0 20px 24px;
          box-shadow: 0 4px 12px rgba(250, 82, 82, 0.15);
          border: 1px solid var(--border-light, #ffdada);
          animation: urgentGlow 3s infinite;
        }

        .Urgent-main-header {
          margin-bottom: 24px;
        }

        .Urgent-main-title {
          font-size: 24px;
          font-weight: 700;
          color: #c92a2a;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .Urgent-section-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary, #212529);
          margin: 0;
        }

        .Urgent-badge {
          display: inline-block;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          color: white;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .Urgent-table-wrapper {
          overflow-x: auto;
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 2px 6px rgba(0,0,0,0.05);
        }

        .Urgent-table {
          width: 100%;
          border-collapse: collapse;
          min-width: 800px;
        }

        .Urgent-table th {
          background: var(--bg-default, #f8f9fa);
          color: var(--text-secondary, #495057);
          padding: 12px 16px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          text-transform: uppercase;
        }

        .Urgent-table-row {
          background: var(--card-bg, white);
          transition: all 0.2s ease;
        }

        .Urgent-table td {
          padding: 14px 16px;
          font-size: 14px;
          color: var(--text-primary, #212529);
          border-bottom: 1px solid var(--border-light, #eee);
        }

        .Urgent-leave-type {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          background: var(--bg-default, #e9ecef);
          color: var(--text-secondary, #495057);
        }

        .Urgent-actions {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .Urgent-action-buttons {
          display: flex;
          gap: 8px;
        }

        .Urgent-btn-view {
          background: #228be6;
          color: white;
          border: none;
          border-radius: 6px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
        }

        .Urgent-btn-approve,
        .Urgent-btn-reject {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 8px;
          color: white;
          font-weight: bold;
          font-size: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .Urgent-btn-approve {
          background: linear-gradient(135deg, #40c057, #2b8a3e);
          box-shadow: 0 2px 4px rgba(64, 192, 87, 0.3);
        }

        .Urgent-btn-reject {
          background: linear-gradient(135deg, #fa5252, #c92a2a);
          box-shadow: 0 2px 4px rgba(250, 82, 82, 0.3);
        }

        /* Shake Animation */
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-3px); }
          20%, 40%, 60%, 80% { transform: translateX(3px); }
        }

        .shake {
          animation: shake 2s infinite;
        }

        .shake:hover {
          animation-play-state: paused;
          transform: scale(1.1);
        }

        @keyframes urgentGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(250, 82, 82, 0.2); }
          50% { box-shadow: 0 0 0 8px rgba(250, 82, 82, 0); }
        }
      `}</style>
    </div>
  );
};

export default UrgentLeaveRequests;
